// Clinic opening hours as C-10 shows them (mockup `lib/clinicHours.ts`, rewritten over API.md's `HH:mm` DayHours).
import type { DayHours } from './types'

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const
const SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
/** Monday first, the way clinics think about the week. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const

export const toMinutes = (t: string): number => {
  const [h = '0', m = '0'] = t.split(':')
  return Number(h) * 60 + Number(m)
}
export const toTime = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

/** `HH:mm` → "8:00 AM". */
export function fmtTime(t: string): string {
  const m = toMinutes(t)
  const h = Math.floor(m / 60)
  return `${String(h % 12 || 12)}:${String(m % 60).padStart(2, '0')} ${h < 12 || h === 24 ? 'AM' : 'PM'}`
}

/** 5:00 AM to 11:00 PM in 30-minute steps (UserFlows C-10). */
export const TIME_OPTIONS = Array.from({ length: 37 }, (_, i) => toTime(300 + i * 30)).map((t) => ({
  value: t,
  label: fmtTime(t),
}))

/** The day's row, by weekday (the API sends them sorted 0..6). */
export const dayOf = (hours: readonly DayHours[], weekday: number): DayHours | undefined =>
  hours.find((h) => h.weekday === weekday)

const same = (a: DayHours | undefined, b: DayHours | undefined) =>
  !!a && !!b && a.open === b.open && (!a.open || (a.start === b.start && a.end === b.end))

/** "Mon–Fri 8:00 AM – 5:00 PM", "Sat 8:00 AM – 12:00 PM", "Sun Closed": consecutive days with the same hours. */
export function hoursSummary(hours: readonly DayHours[]): string[] {
  const out: string[] = []
  for (let i = 0; i < WEEK_ORDER.length;) {
    const d = WEEK_ORDER[i] ?? 0
    let j = i
    while (j + 1 < WEEK_ORDER.length && same(dayOf(hours, WEEK_ORDER[j + 1] ?? 0), dayOf(hours, d))) j++
    const days = i === j ? SHORT[d] : `${SHORT[d]}–${SHORT[WEEK_ORDER[j] ?? 0]}`
    const h = dayOf(hours, d)
    out.push(`${days} ${h?.open ? `${fmtTime(h.start)} – ${fmtTime(h.end)}` : 'Closed'}`)
    i = j + 1
  }
  return out
}
